import { plural } from './plural'

/** Strings owned by the thread components (C6) and the thread view (S5, K2). */
export const threadCopy = {
  message: { retry: 'Retry', reasoning: 'thinking' },
  markdown: { codeTitle: 'code', imageFallback: 'Image' },
  toolCall: {
    state: { running: 'running', ok: 'ok', failed: 'failed' },
    arguments: 'Arguments',
    result: 'Result',
    argumentsCaption: 'arguments',
    resultCaption: 'result',
    showAll: (lines: number) => `Show all ${lines.toLocaleString()} lines`,
    showFewer: 'Show fewer lines',
  },
  composer: {
    placeholder: (label: string) => `${label}…`,
    newMessageIn: (name: string) => `New message in ${name}`,
    newThreadIn: (name: string) => `New thread in ${name}`,
    thisThread: 'this thread',
    thisTopic: 'this topic',
    send: 'Send',
    stop: 'Stop',
    retry: 'Retry',
    suggestions: 'Suggestions',
    noMatches: 'No matches',
    suggestionsFailed: 'Could not load suggestions.',
    groups: {
      agents: 'Agents',
      people: 'People',
      commands: 'Commands',
      resources: 'Resources',
    },
    selectedMentions: 'Selected agent mentions',
    mention: (name: string) => `@${name}`,
    frozenUnknown: 'Retry the message above.',
    unknownSend: 'Could not confirm the message was received.',
    sendFailed: 'Failed to send message — try again.',
    stopFailed: 'Failed to stop the agents — try again.',
  },
  view: {
    details: 'Details',
    detailsTitle: 'Thread details',
    rename: 'Rename',
    allThreads: 'All threads',
    titleLabel: 'Thread title',
    titleFallback: 'Thread',
    reopen: 'Reopen',
    resolve: 'Resolve',
    archive: 'Archive',
    messages: (n: number) => plural(n, { one: 'message', other: 'messages' }),
    resources: (n: number) =>
      plural(n, { one: 'resource', other: 'resources' }),
    agents: (n: number) => plural(n, { one: 'agent', other: 'agents' }),
    titledBy: 'Titled by',
    loadFailed: 'Could not load this thread.',
    resourcesFailed: 'Could not load resources.',
    resourceFailed: 'Could not load this resource.',
    empty: 'No messages in this thread yet.',
    agentFallback: 'The agent',
    waitingOnYou: (agent: string) => `${agent} is waiting on your reply.`,
    waitingOn: (agent: string, person: string) =>
      `${agent} is waiting on ${person}'s reply.`,
    truncated: 'Reply cut off at the output limit',
    continue: 'Continue',
    scrollToLatest: 'Scroll to latest message',
    renameFailed: 'Failed to rename thread — try again.',
    reopenFailed: 'Failed to reopen thread — try again.',
    archiveFailed: 'Failed to archive thread — try again.',
    resolveFailed: 'Failed to resolve thread — try again.',
  },
  panel: {
    thread: 'Thread',
    status: 'Status',
    topic: 'Topic',
    started: 'Started',
    messages: 'Messages',
    participants: 'Participants',
    resources: 'Resources',
    thisThread: 'This thread',
    from: (topic: string) => `From ${topic}`,
    workspace: 'Workspace',
    empty: 'No resources in scope.',
    allTopicResources: 'All topic resources',
  },
  review: {
    open: 'Open',
    contents: 'Contents',
    deleted: 'Resource deleted',
    answer: 'Answer',
    noteForAgent: 'Note for the agent',
    notes: 'Notes',
    optional: '(optional)',
    requestChanges: 'Request changes',
    approve: 'Approve',
    status: {
      needsApproval: 'needs approval',
      awaitingAnswer: 'awaiting answer',
      approved: 'approved',
      changesRequested: 'changes requested',
      answered: 'answered',
    },
    outcome: {
      approved: 'Approved',
      rejected: 'Changes requested',
      answered: 'Answered',
    },
    decision: (outcome: string, by?: string, at?: string) =>
      [by ? `${outcome} by ${by}` : outcome, at].filter(Boolean).join(' · '),
    expired: 'Expired without a decision',
    approvedBy: (by: string) => `${by} approved the plan`,
    answeredBy: (by: string) => `${by} answered`,
    changesRequestedBy: (by: string) => `${by} requested changes`,
    resuming: (line: string, agent: string) => `${line} · ${agent} is resuming`,
    failed: {
      approve: 'Failed to approve the plan — try again.',
      reject: 'Failed to request changes — try again.',
      answer: 'Failed to send the answer — try again.',
    },
  },
  resolve: {
    title: 'Keep or move thread resources',
    body: 'Move the ones the topic should keep using.',
    keep: 'Keep',
    move: 'Move',
    archive: 'Archive',
    done: 'Done',
    keepRemaining: 'Keep remaining',
    outcome: {
      kept: 'Kept',
      moved: 'Moved',
      archived: 'Archived',
      error: 'Failed',
    },
    moveFailed: (name: string) =>
      `Failed to move ${name} — open it from the thread details and try again.`,
    archiveFailed: (name: string) =>
      `Failed to archive ${name} — reopen the thread and resolve it again.`,
  },
} as const
